import React, { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    AccountBookOutlined,
    ApartmentOutlined,
    CalculatorOutlined,
    ControlOutlined,
    DisconnectOutlined,
    ScheduleOutlined,
    HistoryOutlined,
    InboxOutlined,
    MergeCellsOutlined,
    PartitionOutlined,
    ToolOutlined,
    TruckOutlined,
} from '@ant-design/icons';
import MainLayout from 'layouts/MainLayout';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { findFirstAvailableMenuItem } from 'utils/menuUtils';

const OpsToolLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const menuItems = [
        {
            key: 'settlement',
            label: '结算',
            icon: <AccountBookOutlined />,
            display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
            children: [
                {
                    key: 'settlement/bill-ops',
                    label: '账单工具',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/bill-ops'),
                },
                {
                    key: 'settlement/price-center/fee-item-factor-config',
                    label: '费项因子配置',
                    icon: <CalculatorOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/price-center/fee-item-factor-config'),
                },
                {
                    key: 'settlement/workflow-template-designer',
                    label: '流程模板设计',
                    icon: <ApartmentOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/workflow-template-designer'),
                },
                {
                    key: 'settlement/bcp/check-jobs',
                    label: '核对任务',
                    icon: <ControlOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/bcp/check-jobs'),
                },
                {
                    key: 'settlement/bcp/check-results',
                    label: '差错处理',
                    icon: <ControlOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/bcp/check-results'),
                },
                {
                    key: 'settlement/bcp/user-groups',
                    label: '用户组管理',
                    icon: <ControlOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/bcp/user-groups'),
                },
                {
                    key: 'settlement/bcp/job-versions',
                    label: '任务历史版本',
                    icon: <HistoryOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/bcp/job-versions'),
                },
                {
                    key: 'settlement/data-maintenance',
                    label: '结算数据维护',
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/data-maintenance'),
                },
                {
                    key: 'settlement/tmp-run-task-rerun',
                    label: 'tmp_run_task重跑',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/tmp-run-task-rerun'),
                },
                {
                    key: 'settlement/audit-log',
                    label: '结算审计日志',
                    icon: <HistoryOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/audit-log'),
                },
                {
                    key: 'settlement/dsp-driver-bill',
                    label: '司机结算账单',
                    icon: <AccountBookOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/dsp-driver-bill'),
                },
                {
                    key: 'settlement/table-split-compensation',
                    label: '大表拆分补偿',
                    icon: <PartitionOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/table-split-compensation'),
                },
                {
                    key: 'settlement/dsp-billing-cadence-whitelist',
                    label: 'DSP 账单节奏白名单',
                    icon: <ControlOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]),
                    onClick: () => navigate('/ops-tool/settlement/dsp-billing-cadence-whitelist'),
                },
            ],
        },
        {
            key: 'pickup',
            label: '揽收',
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.menu.OPS_TOOL_PICKUP]),
            children: [
                {
                    key: 'pickup/force-merge',
                    label: '强制合并站点',
                    icon: <MergeCellsOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_PICKUP]),
                    onClick: () => navigate('/ops-tool/pickup/force-merge'),
                },
                {
                    key: 'pickup/mark-unreachable',
                    label: '确认无法建联',
                    icon: <DisconnectOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_PICKUP]),
                    onClick: () => navigate('/ops-tool/pickup/mark-unreachable'),
                },
            ],
        },
        {
            key: 'wms',
            label: 'WMS',
            icon: <ToolOutlined />,
            display: hasResource([ResourceCode.menu.OPS_TOOL_WMS]),
            children: [
                {
                    key: 'wms/pickup-hub-data-fix',
                    label: '揽收仓订正',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_WMS]),
                    onClick: () => navigate('/ops-tool/wms/pickup-hub-data-fix'),
                },
                {
                    key: 'wms/sorter-api-tool',
                    label: '分拣机接口调试',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_WMS]),
                    onClick: () => navigate('/ops-tool/wms/sorter-api-tool'),
                },
                {
                    key: 'wms/order-wms-column-fix',
                    label: 'order_wms 数据订正',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_WMS]),
                    onClick: () => navigate('/ops-tool/wms/order-wms-column-fix'),
                },
                {
                    key: 'wms/tt-location-config',
                    label: '分拣配置',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_WMS]),
                    onClick: () => navigate('/ops-tool/wms/tt-location-config'),
                },
                {
                    key: 'wms/data-retention',
                    label: '数据保留清理',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_WMS]),
                    onClick: () => navigate('/ops-tool/wms/data-retention'),
                },
            ],
        },
        {
            key: 'tms',
            label: 'TMS',
            icon: <TruckOutlined />,
            display: hasResource([ResourceCode.menu.OPS_TOOL_TMS]),
            children: [
                {
                    key: 'tms/external-station',
                    label: '外部站点',
                    icon: <TruckOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_TMS]),
                    onClick: () => navigate('/ops-tool/tms/external-station'),
                },
                {
                    key: 'tms/dispatch-tool',
                    label: '调度工具',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_TMS]),
                    onClick: () => navigate('/ops-tool/tms/dispatch-tool'),
                },
                {
                    key: 'tms/task-management',
                    label: '任务工具',
                    icon: <ScheduleOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_TMS]),
                    onClick: () => navigate('/ops-tool/tms/task-management'),
                },
                {
                    key: 'tms/loading-archive',
                    label: '装车数据归档',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_TMS]),
                    onClick: () => navigate('/ops-tool/tms/loading-archive'),
                },
                {
                    key: 'tms/loading-policy',
                    label: '装车策略配置',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_TMS]),
                    onClick: () => navigate('/ops-tool/tms/loading-policy'),
                },
                {
                    key: 'tms/i18n-config',
                    label: '文案配置',
                    icon: <ToolOutlined />,
                    display: hasResource([ResourceCode.menu.OPS_TOOL_TMS]),
                    onClick: () => navigate('/ops-tool/tms/i18n-config'),
                },
            ],
        },
    ];

    useEffect(() => {
        const isRoot = location.pathname === '/ops-tool' || location.pathname === '/ops-tool/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
    }, [location.pathname]);

    return (
        <MainLayout activeFirstMenu="ops-tool" secondMenu={menuItems}>
            <div className="p-6 h-full flex flex-col">
                <Outlet />
            </div>
        </MainLayout>
    );
};

export default OpsToolLayout;
